import React, { useEffect, useRef, useState } from 'react';
import { useGetMeQuery, useLazySwitchRoleQuery } from './Auth.api';
import { useAppSelector } from '../../../../hooks/ReduxHook';
import { AUTH_LOCAL_STORAGE_KEY, authRoutes } from './constants';
import UseNavigation from '../../../../hooks/useNavigation';
import { useAccountSwitcher } from '../../Users/subcomponents/AccountSwitcher/logic/useAccountSwitcher';
import { UserGroup } from '../../Users/logic/Users.types';
import {
  getSetupMarker,
  isSetupSubmissionInProgress,
  readUrlCallback,
  resumePendingCallback,
  savePendingCallback,
} from './accountSetupStorage';

const getStatus = (error: unknown) =>
  typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined;

// The admin check for pages. Unlike useAuthorizationLogic, it has no routing side
// effects, so a page can use it without starting another role switch or redirect.
export const useIsAdmin = () => {
  const { auth, accountSwitcher } = useAppSelector((state) => state);
  const activeRole = auth.user?.roles?.find((role) => role.id === accountSwitcher?.activeRoleId);
  return activeRole?.group === UserGroup.Admin;
};

// Runs the app-level account load and routing. Use it once, from useAppInitiate.
export const useAuthorizationLogic = () => {
  const { auth, base } = useAppSelector((state) => state);
  const { gotoPage } = UseNavigation();
  const [callGetRoleApi, { isLoading: isLoadingGetRole }] = useLazySwitchRoleQuery();
  const {
    getActiveRole,
    doSetActiveRoleId,
    doClearActiveRoleId,
    doToggleAccountSwitcher,
    doOpenCreateAccount,
    refetch: refetchRoleList,
  } = useAccountSwitcher();
  const { isLoading: isLoadingUser, refetch: refetchMe } = useGetMeQuery();
  const [roleLoadFailed, setRoleLoadFailed] = useState(false);
  const [routingAttempt, setRoutingAttempt] = useState(0);
  // Set from the start of a role-list reconciliation until both refetches
  // succeed, for the session that started it. The routing waits, because a getMe
  // refetch updates the user before the role list is known.
  const reconcilingRef = useRef<{ sessionToken: string | null } | null>(null);
  // True while a reconciliation holds the routing for the current session. A
  // hold from a previous session is dropped, so it cannot block the next user.
  const isReconciling = () => {
    const hold = reconcilingRef.current;
    if (hold && hold.sessionToken !== localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)) {
      reconcilingRef.current = null;
    }
    return reconcilingRef.current !== null;
  };

  // A getMe for the current session that fails with a 5xx or a network error is
  // not a user with 0 roles. The app shows a retry screen instead. The auth
  // middleware sets this only for a request sent with the current token.
  const accountLoadFailed = auth.accountLoadFailed;

  // An unresolved setup for the loaded user, or one that a submission in this
  // session found before any user was loaded. A marker for another username is
  // not applied. The stored marker of a submission still in progress in this
  // page is not unresolved yet: the form stays mounted until the result.
  const username = auth.isUserAuthenticated ? auth.user?.username : undefined;
  const sessionMarker = auth.setupMarker && (!username || auth.setupMarker.username === username) ? auth.setupMarker : null;
  const setupMarker = sessionMarker ?? (username && !isSetupSubmissionInProgress(username) ? getSetupMarker(username) : null);

  const roles = auth.user?.roles;
  const roleIdsKey = Array.isArray(roles) ? roles.map((role) => role.id).join(',') : undefined;

  // The single role is stale. Refresh both role lists, so the switcher does not
  // offer it again. A failed refresh stays an error: it keeps the routing
  // suspended, never shows an empty list, and never opens "Create account".
  const reconcileRoles = async () => {
    const sessionToken = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
    const hold = { sessionToken };
    reconcilingRef.current = hold;
    const sessionChanged = () => localStorage.getItem(AUTH_LOCAL_STORAGE_KEY) !== sessionToken;
    const releaseHold = () => {
      if (reconcilingRef.current === hold) {
        reconcilingRef.current = null;
      }
    };
    let me: { username?: unknown; roles?: unknown } | undefined;
    try {
      [me] = await Promise.all([refetchMe().unwrap(), refetchRoleList().unwrap()]);
    } catch {
      if (sessionChanged()) {
        releaseHold();
        return;
      }
      // The hold stays until a retry of both refetches succeeds.
      setRoleLoadFailed(true);
      return;
    }
    releaseHold();
    if (sessionChanged()) {
      return;
    }
    // An unresolved setup replaces the routing (P6).
    if (typeof me?.username === 'string' && getSetupMarker(me.username)) {
      return;
    }
    // 0 roles in a successful getMe opens "Create account" (P2).
    if (Array.isArray(me?.roles) && me?.roles.length === 0) {
      doClearActiveRoleId();
      doOpenCreateAccount();
      return;
    }
    doToggleAccountSwitcher(true);
  };

  const switchOnLoad = async (roleId: string, source: 'single-role' | 'stored', switchUsername: string) => {
    const sessionToken = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
    const result = await callGetRoleApi({ roleId });
    // A result for a previous session must not change the current session.
    if (localStorage.getItem(AUTH_LOCAL_STORAGE_KEY) !== sessionToken) {
      return;
    }
    if (!result.isError) {
      // Save the role only after the switch succeeds.
      doSetActiveRoleId(roleId);
      resumePendingCallback(switchUsername, sessionToken);
      return;
    }
    const status = getStatus(result.error);
    if (status !== 403 && status !== 404) {
      // A 503 or a network error keeps the stored ID and offers a retry.
      setRoleLoadFailed(true);
      return;
    }
    if (source === 'stored') {
      doClearActiveRoleId();
      doToggleAccountSwitcher(true);
      return;
    }
    await reconcileRoles();
  };

  // Runs when the user or its roles change, not on every getMe.
  useEffect(() => {
    if (!auth.isUserAuthenticated || !auth.user || !Array.isArray(roles)) {
      return;
    }
    if (isReconciling()) {
      return;
    }
    // An unresolved setup replaces the routing.
    if (setupMarker) {
      return;
    }
    const loadedUsername = auth.user.username;
    // A signed-in user can arrive with a callback. Keep it for this user until a
    // role is active.
    const arrivalCallback = readUrlCallback();
    if (arrivalCallback && loadedUsername) {
      savePendingCallback(loadedUsername, arrivalCallback);
    }
    if (roles.length === 0) {
      doClearActiveRoleId();
      doOpenCreateAccount();
      return;
    }
    if (roles.length === 1) {
      switchOnLoad(roles[0].id, 'single-role', loadedUsername);
      return;
    }
    const storedRoleId = getActiveRole();
    if (storedRoleId !== null) {
      switchOnLoad(storedRoleId, 'stored', loadedUsername);
    } else {
      doToggleAccountSwitcher(true);
    }
  }, [auth.isUserAuthenticated, username, roleIdsKey, routingAttempt]);

  const retryLoad = () => {
    // A pending reconciliation retries both refetches, even when only its getMe
    // failed.
    if (isReconciling()) {
      setRoleLoadFailed(false);
      reconcileRoles();
      return;
    }
    if (accountLoadFailed) {
      refetchMe();
      return;
    }
    setRoleLoadFailed(false);
    setRoutingAttempt((attempt) => attempt + 1);
  };

  // The unresolved setup screen comes before these redirects.
  if (!setupMarker) {
    if (authRoutes.includes(window.location.pathname) && auth.isUserAuthenticated) {
      setTimeout(() => {
        gotoPage('/');
      }, 100);
    } else {
      if (window.location.pathname === '/' && base.landUrl) {
        setTimeout(() => {
          gotoPage(base.landUrl);
        }, 100);
      }
    }
  }

  return {
    isUserAuthenticated: auth.isUserAuthenticated,
    isLoading: isLoadingUser || isLoadingGetRole,
    accountLoadFailed,
    roleLoadFailed,
    retryLoad,
    setupMarker,
  };
};
